import { apiClient } from './client';
import type { ApiResponse, PagedResult } from '@/types/api.types';
import type {
  WorkflowDefinitionDto,
  WorkflowListDto,
  WorkflowFilterDto,
  WorkflowInstanceDto,
  CreateWorkflowRequest,
  UpdateWorkflowRequest,
  AddWorkflowLevelRequest,
} from '@/types/workflow.types';

export const workflowApi = {
  // ── Workflow Definitions ────────────────────────────────────────────────────

  /** GET /api/v1/workflows — paginated list */
  getList: (filter?: WorkflowFilterDto) =>
    apiClient.get<ApiResponse<PagedResult<WorkflowListDto>>>('/workflows', { params: filter }),

  /** GET /api/v1/workflows/{id} — full definition with levels */
  getById: (id: string) =>
    apiClient.get<ApiResponse<WorkflowDefinitionDto>>(`/workflows/${id}`),

  /** POST /api/v1/workflows — create new workflow definition */
  create: (data: CreateWorkflowRequest) =>
    apiClient.post<ApiResponse<{ id: string }>>('/workflows', data),

  /** PUT /api/v1/workflows/{id} — update header */
  update: (id: string, data: UpdateWorkflowRequest) =>
    apiClient.put<ApiResponse<object>>(`/workflows/${id}`, data),

  /** DELETE /api/v1/workflows/{id} — soft delete */
  delete: (id: string) =>
    apiClient.delete<ApiResponse<object>>(`/workflows/${id}`),

  /** POST /api/v1/workflows/{id}/activate — activate and optionally set as default */
  activate: (id: string, setAsDefault = false) =>
    apiClient.post<ApiResponse<object>>(`/workflows/${id}/activate`, { setAsDefault }),

  /** POST /api/v1/workflows/{id}/deactivate — deactivate */
  deactivate: (id: string) =>
    apiClient.post<ApiResponse<object>>(`/workflows/${id}/deactivate`),

  // ── Workflow Levels ─────────────────────────────────────────────────────────

  /** POST /api/v1/workflows/{id}/levels — add approval level */
  addLevel: (id: string, data: AddWorkflowLevelRequest) =>
    apiClient.post<ApiResponse<object>>(`/workflows/${id}/levels`, data),

  /** DELETE /api/v1/workflows/{id}/levels/{levelId} — remove approval level by its GUID */
  deleteLevel: (id: string, levelId: string) =>
    apiClient.delete<ApiResponse<object>>(`/workflows/${id}/levels/${levelId}`),

  // ── Workflow Instances ──────────────────────────────────────────────────────

  /** GET /api/v1/workflows/instances?entityName=X&entityId=Y — instance for entity */
  getInstance: (entityName: string, entityId: string) =>
    apiClient.get<ApiResponse<WorkflowInstanceDto>>('/workflows/instances', {
      params: { entityName, entityId },
    }),
};
